import React, { useState, useEffect } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import api from '../../utils/api'
import Spinner from '../Spinner'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)


const ServiceOrderChart = ({ data: propData }) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [dateFilter, setDateFilter] = useState('month')
  const [isDarkMode, setIsDarkMode] = useState(false)

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'))
    }

    checkDarkMode()

    // Watch for theme changes
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])

  // Fetch service orders data
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true)
      try {
        // Calculate date range based on filter
        const endDate = new Date()
        const startDate = new Date()

        if (dateFilter === 'month') {
          startDate.setDate(startDate.getDate() - 30) // Last 30 days
        } else if (dateFilter === 'week') {
          startDate.setDate(startDate.getDate() - 7) // Last 7 days
        } else if (dateFilter === 'year') {
          startDate.setFullYear(startDate.getFullYear() - 1) // Last year
        }

        const params = {
          page_size: 1000, // Get all orders for the period
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
        }

        const res = await api.get('service-orders/orders/', { params })
        const ordersData = res.data.results || res.data || []
        setOrders(ordersData)
      } catch (err) {
        console.error('Error fetching service orders:', err)
        setOrders([])
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [dateFilter])

  // Transform orders data into chart format
  const transformOrdersToChartData = () => {
    if (!orders || orders.length === 0) {
      return {
        labels: [],
        data: []
      }
    }

    // Group orders by date
    const ordersByDate = {}

    orders.forEach(order => {
      if (order?.created_at) {
        const date = new Date(order.created_at)
        // Use ISO date string for sorting, then format for display
        const dateKey = date.toISOString().split('T')[0] // YYYY-MM-DD format

        if (!ordersByDate[dateKey]) {
          ordersByDate[dateKey] = {
            count: 0,
            displayDate: date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })
          }
        }
        ordersByDate[dateKey].count += 1
      }
    })

    // Sort by date (using ISO date string) and extract labels and data
    const sortedEntries = Object.entries(ordersByDate).sort((a, b) => {
      return a[0].localeCompare(b[0]) // Sort by ISO date string
    })

    const labels = sortedEntries.map(([, value]) => value.displayDate)
    const data = sortedEntries.map(([, value]) => value.count)

    return { labels, data }
  }

  // Use prop data if provided, otherwise use fetched data
  const chartDataFromProps = propData?.dataset?.label && propData?.dataset?.data
    ? {
      labels: propData.dataset.label.map(label => label?.slice(0, 3)),
      data: propData.dataset.data
    }
    : null

  const chartDataFromOrders = transformOrdersToChartData()
  const finalChartData = chartDataFromProps || chartDataFromOrders

  const chartData = {
    labels: finalChartData.labels,
    datasets: [
      {
        label: 'Orders',
        data: finalChartData.data,
        borderColor: '#FFB733',
        backgroundColor: 'rgba(255, 183, 51, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#FFB733',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#FFB733',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
        text: 'Orders',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
        },
      },
    },
    backgroundColor: '#fff',
  }

  return <div className="-m-1.5 overflow-x-auto">
    <div className="p-1.5 min-w-full inline-block align-middle">
      <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
        <div className="bg-white dark:bg-dark border-b border-gray-200 dark:border-border-dark p-4 flex flex-row items-center justify-between">
          <h1 className='text-lg font-bold'>
            Orders
          </h1>
          <select
            name="filter"
            id="filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="py-2 capitalize border-none px-5 pe-9 bg-gray-100 dark:bg-secondaryDark rounded-lg text-sm focus:border-white focus:ring-white"
          >
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="year">Last year</option>
          </select>
        </div>
        <div className='p-4 bg-white dark:bg-dark'>
          {loading ? (
            <div className="flex justify-center items-center h-56">
              <Spinner />
            </div>
          ) : finalChartData.labels.length === 0 ? (
            <div className="flex justify-center items-center h-56 text-gray-500 dark:text-gray-400">
              No orders found for the selected period
            </div>
          ) : (
            <Line data={chartData} options={options} />
          )}
        </div>
      </div>
    </div>
  </div>
}

export default ServiceOrderChart